/**
 * Contact Form Manager - Moon Moon Agency
 * Gère soumission, upload de fichiers (file.io → URL dans le message), succès, select sujet.
 */
const ContactFormManager = {
    config: {
        selectors: {
            container: '[data-section-contact-id]',
            form: '.mm-contact-form',
            submitBtn: '[data-trigger-submit="true"]',
            submitInput: '.mm-contact-submit-input',
            fileInput: '#contact_files',
            fileList: '.mm-contact-attachment-file-list',
            subjectSelect: '#contact_subject',
            messageTextarea: '#contact_message',
            orderField: '[data-contact-order-field]',
        },
        classes: {
            none: 'mm-none',
            loading: 'mm-contact-form--loading',
            placeholderActive: 'mm-contact-reasons-selector--placeholder',
        },
    },

    _skipFileUpload: false,

    init() {
        if (!window.getElement(this.config.selectors.container)) return;
        this._bindEvents();
        this._prefillFromUrl();
        this._initSelectState();
        window.log('info', '📬 [CONTACT_FORM]', 'Initialized');
    },

    _bindEvents() {
        document.addEventListener('click', (e) => {
            if (!e.target.closest(this.config.selectors.submitBtn)) return;
            e.preventDefault();
            const submitInput = window.getElement(this.config.selectors.submitInput);
            if (submitInput) submitInput.click();
        });

        const form = window.getElement(this.config.selectors.form);
        if (form) form.addEventListener('submit', (e) => this._handleFormSubmit(e));

        const fileInput = window.getElement(this.config.selectors.fileInput);
        if (fileInput) fileInput.addEventListener('change', (e) => this._updateFileList(e.target));

        const select = window.getElement(this.config.selectors.subjectSelect);
        if (select) {
            select.addEventListener('change', (e) => {
                e.target.classList.remove(this.config.classes.placeholderActive);
                this._updateOrderFieldVisibility(e.target.value);
            });
        }
    },

    async _handleFormSubmit(e) {
        if (this._skipFileUpload) {
            this._skipFileUpload = false;
            return;
        }

        const fileInput = window.getElement(this.config.selectors.fileInput);
        if (!fileInput?.files.length) return;

        e.preventDefault();

        const form = window.getElement(this.config.selectors.form);
        form.classList.add(this.config.classes.loading);

        try {
            const uploaded = await this._uploadFiles(fileInput.files);
            this._appendUrlsToMessage(uploaded);
            fileInput.value = '';
            this._updateFileList(fileInput);
            window.log('info', '📬 [CONTACT_FORM]', `${uploaded.length} fichier(s) uploadé(s)`);
        } catch (err) {
            window.log('error', '📬 [CONTACT_FORM]', 'Upload échoué, envoi sans pièces jointes', { error: err.message });
        } finally {
            form.classList.remove(this.config.classes.loading);
        }

        this._skipFileUpload = true;
        const submitInput = window.getElement(this.config.selectors.submitInput);
        if (submitInput) submitInput.click();
    },

    async _uploadFiles(files) {
        return Promise.all(
            Array.from(files).map(async (file) => {
                const body = new FormData();
                body.append('file', file);

                const res = await fetch('https://file.io/', { method: 'POST', body });
                if (!res.ok) throw new Error(`Upload failed: ${file.name}`);
                const data = await res.json();
                if (!data.success) throw new Error(`Upload error: ${file.name}`);

                return { name: file.name, url: data.link };
            })
        );
    },

    _appendUrlsToMessage(uploaded) {
        const textarea = window.getElement(this.config.selectors.messageTextarea);
        if (!textarea || !uploaded.length) return;
        const links = uploaded.map(({ name, url }) => `${name} : ${url}`).join('\n');
        const sep = textarea.value.trim() ? '\n\n' : '';
        textarea.value += `${sep}--- Pièce(s) jointe(s) ---\n${links}`;
    },

    /**
     * Pre-remplit sujet + message depuis ?subject= / ?message= (lien "Verifier la disponibilite"
     * de la fiche produit, cf. mm-btn-check-availability.liquid). Le sujet n'est selectionne que
     * s'il existe dans les options configurees dans le customizer.
     */
    _prefillFromUrl() {
        const params = new URLSearchParams(window.location.search);
        const subject = params.get('subject');
        const message = params.get('message');

        const select = window.getElement(this.config.selectors.subjectSelect);
        if (subject && select) {
            const option = Array.from(select.options).find((o) => o.value.toLowerCase() === subject.trim().toLowerCase());
            if (option) {
                select.value = option.value;
                select.classList.remove(this.config.classes.placeholderActive);
            }
        }

        const textarea = window.getElement(this.config.selectors.messageTextarea);
        if (message && textarea && !textarea.value) textarea.value = message;
    },

    _initSelectState() {
        const select = window.getElement(this.config.selectors.subjectSelect);
        if (!select) return;
        if (select.value === '') select.classList.add(this.config.classes.placeholderActive);
        this._updateOrderFieldVisibility(select.value);
    },

    _updateOrderFieldVisibility(subjectValue) {
        const orderField = window.getElement(this.config.selectors.orderField);
        if (!orderField) return;
        const hiddenSubjects = (orderField.dataset.contactOrderHideSubjects || '').split(',').map((s) => s.trim());
        orderField.classList.toggle(this.config.classes.none, hiddenSubjects.includes(subjectValue));
    },

    _updateFileList(input) {
        const listEl = window.getElement(this.config.selectors.fileList);
        if (!listEl) return;

        if (!input.files.length) {
            listEl.classList.add(this.config.classes.none);
            listEl.textContent = '';
            return;
        }

        listEl.textContent = Array.from(input.files).map(f => f.name).join(', ');
        listEl.classList.remove(this.config.classes.none);
    },
};

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => ContactFormManager.init());
} else {
    ContactFormManager.init();
}

window.addEventListener('resize', () => {});
